import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useStandalone } from './useStandalone'
import { useIsMobile } from './useIsMobile'

// iOS-style edge-swipe-to-go-back. Triggers navigate(-1) when the user
// pulls horizontally from the leading edge past a threshold. No visual
// affordance — the page change itself is the feedback.
//
// Gating: standalone PWA + mobile only. In a regular browser tab the
// platform back button covers this; on desktop the mouse can't perform
// the gesture anyway.
const EDGE_ZONE_PX = 20
const COMMIT_DISTANCE_PX = 80
const MAX_VERTICAL_DRIFT_PX = 40
const MAX_DURATION_MS = 800

// Cancel if the touch starts inside a horizontally-scrollable element —
// e.g. the workout SetLogger chip rows. Otherwise the gesture would
// fight the scroll and feel broken.
function startedInsideHorizontalScroll(target) {
  let node = target
  while (node && node !== document.body) {
    if (node instanceof HTMLElement) {
      const styles = window.getComputedStyle(node)
      const overflowX = styles.overflowX
      const scrollable =
        (overflowX === 'auto' || overflowX === 'scroll') &&
        node.scrollWidth > node.clientWidth
      if (scrollable) return true
    }
    node = node.parentNode
  }
  return false
}

export function useSwipeBack() {
  const navigate = useNavigate()
  const { i18n } = useTranslation()
  const isStandalone = useStandalone()
  const isMobile = useIsMobile()
  const enabled = isStandalone && isMobile

  useEffect(() => {
    if (!enabled) return undefined

    const isRTL = i18n.language === 'ar'

    let startX = 0
    let startY = 0
    let startTime = 0
    let active = false

    const onTouchStart = (e) => {
      if (e.touches.length !== 1) return
      const t = e.touches[0]
      const w = window.innerWidth
      // Leading edge: left in LTR, right in RTL.
      const fromLeadingEdge = isRTL
        ? t.clientX >= w - EDGE_ZONE_PX
        : t.clientX <= EDGE_ZONE_PX
      if (!fromLeadingEdge) return
      if (startedInsideHorizontalScroll(e.target)) return

      startX = t.clientX
      startY = t.clientY
      startTime = Date.now()
      active = true
    }

    const onTouchMove = (e) => {
      if (!active || e.touches.length !== 1) return
      const t = e.touches[0]
      const dy = Math.abs(t.clientY - startY)
      // Diagonal? Bail. Lets vertical scrolls win cleanly.
      if (dy > MAX_VERTICAL_DRIFT_PX) {
        active = false
      }
    }

    const onTouchEnd = (e) => {
      if (!active) return
      active = false
      const t = e.changedTouches[0]
      if (!t) return
      const elapsed = Date.now() - startTime
      if (elapsed > MAX_DURATION_MS) return

      const dx = t.clientX - startX
      const dy = Math.abs(t.clientY - startY)
      if (dy > MAX_VERTICAL_DRIFT_PX) return

      // Pull direction must match the back direction: rightward in LTR,
      // leftward in RTL.
      const pulled = isRTL ? -dx : dx
      if (pulled < COMMIT_DISTANCE_PX) return

      // history.length includes the current entry; > 1 means there's
      // something to go back to. SPA-internal nav increments this.
      if (window.history.length > 1) {
        navigate(-1)
      }
    }

    // passive: true on start/move; end must be non-passive to allow
    // hypothetical preventDefault later. (We don't call it today.)
    document.addEventListener('touchstart', onTouchStart, { passive: true })
    document.addEventListener('touchmove', onTouchMove, { passive: true })
    document.addEventListener('touchend', onTouchEnd, { passive: true })
    document.addEventListener('touchcancel', () => { active = false }, { passive: true })

    return () => {
      document.removeEventListener('touchstart', onTouchStart)
      document.removeEventListener('touchmove', onTouchMove)
      document.removeEventListener('touchend', onTouchEnd)
    }
  }, [enabled, i18n.language, navigate])
}
